import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { cn, reportEvent } from '@/app/utils';
import { useValidation } from '../../hooks/use-validation';
import { EnterpriseIdInput } from './EnterpriseIdInput';
import { InviteCodeInput } from './InviteCodeInput';
import { LoginSeparator } from './LoginSeparator';

const CODE_LENGTH = 6;

const INPUT_CLASS =
  'h-12 w-full rounded-12 border border-system-white12 bg-system-white08 py-[10px] text-sm text-system-white placeholder:text-system-white64';

interface IdentityCodeLoginCardProps {
  /** 账号类型：国内站手机号（+86），海外站邮箱 */
  identityType: 'phone' | 'email';
  errorMsg: string;
  identity: string;
  changeIdentity: (identity: string) => void;
  sending: boolean;
  /** 发送验证码倒计时（秒），> 0 时发送按钮置灰并显示剩余秒数 */
  second: number;
  sendVerifyCode: () => void;
  handleSignInWithVerifyCode: (verifyCode: string) => void;
  /** 验证码校验失败（AuthCodeInvalid）：验证码框标红，修改后通过 onChangeCode 清除 */
  codeError: boolean;
  onChangeCode: () => void;
  inviteCode: string;
  handleValidateInviteCode: (inviteCode: string) => void;
  inviteCodeError: string;
  showInviteCode?: boolean;
  enterpriseId: string;
  enterpriseIdError: string;
  onEnterpriseIdChange: (enterpriseId: string) => void;
  showEnterpriseId: boolean;
  enterpriseIdReadOnly: boolean;
  /** 非首屏时传入：主按钮下方渲染「返回」按钮，回到上一步。 */
  onBack?: () => void;
}

/**
 * 账号 + 验证码单步登录：账号（国内手机号 / 海外邮箱）与验证码（框内右侧发送按钮）同屏填写，
 * 下方用「&」分隔出邀请码，最后统一登录。
 * 发送验证码沿用 Login 的 sendVerifyCode（含人机校验与倒计时），不再跳转独立的验证码页。
 */
export const IdentityCodeLoginCard = ({
  identityType,
  errorMsg: errorMsgProps,
  identity: identityProps,
  changeIdentity,
  sending,
  second,
  sendVerifyCode,
  handleSignInWithVerifyCode,
  codeError,
  onChangeCode,
  inviteCode,
  handleValidateInviteCode,
  inviteCodeError,
  showInviteCode = true,
  enterpriseId,
  enterpriseIdError,
  onEnterpriseIdChange,
  showEnterpriseId,
  enterpriseIdReadOnly,
  onBack,
}: IdentityCodeLoginCardProps) => {
  const t = useTranslations();
  const isPhone = identityType === 'phone';
  const [identity, setIdentity] = useState(identityProps);
  const [identityValid, setIdentityValid] = useState(false);
  const [identityError, setIdentityError] = useState('');
  const [code, setCode] = useState('');

  const { checkEmail, checkPhone } = useValidation({
    onValidationChange: (valid, message) => {
      setIdentityValid(valid);
      setIdentityError(message);
    },
  });

  // 输入校验错误优先，其次是发送/登录接口回来的错误
  const errorMsg = identityError || errorMsgProps;
  // 验证码错误时只标红验证码框，账号框保持原样
  const identityHasError = Boolean(identityError) || (Boolean(errorMsgProps) && !codeError);
  const canSend = identityValid && second <= 0 && !sending;
  const canSubmit = identityValid && code.length === CODE_LENGTH;

  const submit = () => {
    if (!canSubmit) return;
    handleSignInWithVerifyCode(code);
  };

  return (
    <div className="flex w-full max-w-[350px] flex-col items-center gap-5 px-4 pt-10 lg:gap-6 lg:px-0 lg:pt-6">
      {showEnterpriseId && (
        <EnterpriseIdInput
          enterpriseId={enterpriseId}
          enterpriseIdError={enterpriseIdError}
          onEnterpriseIdChange={onEnterpriseIdChange}
          readOnly={enterpriseIdReadOnly}
        />
      )}

      <div className="relative flex w-full items-center text-sm">
        {isPhone && (
          <span className="absolute left-4 inline-flex items-center text-system-white after:ms-2 after:inline-block after:h-4 after:w-[1px] after:bg-system-white">
            +86
          </span>
        )}
        <Input
          inputMode={isPhone ? 'numeric' : 'email'}
          placeholder={t(isPhone ? 'login.phonePlaceholder' : 'login.emailPlaceholder')}
          className={cn(INPUT_CLASS, isPhone && 'px-[60px]', identityHasError && 'border-system-error')}
          value={identity}
          onChange={(e) => {
            const next = e.target.value;
            setIdentity(next);
            // 同步给 Login：发送验证码时从这里取账号（校验未通过时发送按钮置灰，不会误发）
            changeIdentity(next);
            (isPhone ? checkPhone : checkEmail)(next);
          }}
          maxLength={isPhone ? 11 : 100}
        />
      </div>

      <div className="w-full">
        <div className="relative flex w-full items-center">
          <Input
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder={t('login.code.placeholder')}
            className={cn(INPUT_CLASS, 'pr-28', codeError && 'border-system-error')}
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH));
              onChangeCode();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            // 不设 maxLength：粘贴「验证码：123456」这类带前缀的文本时，原生截断会先吃掉数字
          />
          <button
            type="button"
            disabled={!canSend}
            onClick={() => {
              sendVerifyCode();
              reportEvent('click_validation_code');
            }}
            className="absolute right-4 text-sm text-system-white transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:text-system-white40 disabled:hover:opacity-100"
          >
            {second > 0 ? `${second}s` : t('login.code.send')}
          </button>
        </div>
        {errorMsg && <p className="mt-2 text-left text-system-error text-xs">{errorMsg}</p>}
      </div>

      {!showEnterpriseId && showInviteCode && (
        <>
          <LoginSeparator />
          <InviteCodeInput
            handleValidateInviteCode={handleValidateInviteCode}
            inviteCodeError={inviteCodeError}
            inviteCode={inviteCode}
          />
        </>
      )}

      <Button
        disabled={!canSubmit}
        onClick={submit}
        className="h-12 w-full rounded-12 bg-white font-medium text-base text-black disabled:text-black disabled:opacity-60"
      >
        {t('login.login')}
      </Button>
      {onBack && (
        <Button
          variant="text"
          onClick={onBack}
          className="h-12 w-full rounded-12 font-medium text-base text-system-white64 hover:text-system-white"
        >
          {t('login.back')}
        </Button>
      )}
    </div>
  );
};
